import { Group, MultiSelect, Select, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { STORE_SEARCH_PLACEHOLDER, type StoreStatus } from "@/lib/storeSearch";

/**
 * The store filters, as one full-width bar.
 *
 * It sits in the page body rather than the header's `actions` slot so it has
 * the whole container to wrap in: the retailer field holds a pill per
 * selection, and in a narrow fixed-width box those stack and shove the table
 * down the page. Each control is `flex`-sized rather than fixed-width so they
 * share the row when it is wide and reflow instead of overflowing when it is
 * not — with retailer given the largest basis, since it grows the most.
 */
interface StoreFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;

  retailerOptions: string[];
  retailers: string[];
  onRetailersChange: (value: string[]) => void;

  status: StoreStatus | null;
  onStatusChange: (value: StoreStatus | null) => void;
  /** False when no store has a resolved `active`, e.g. inventory failed. */
  statusKnown: boolean;

  timezoneOptions: string[];
  timezone: string | null;
  onTimezoneChange: (value: string | null) => void;
}

export function StoreFilterBar({
  search,
  onSearchChange,
  retailerOptions,
  retailers,
  onRetailersChange,
  status,
  onStatusChange,
  statusKnown,
  timezoneOptions,
  timezone,
  onTimezoneChange,
}: StoreFilterBarProps) {
  return (
    <Group wrap="wrap" gap="sm" align="flex-end" mb="md" w="100%">
      <TextInput
        aria-label="Search stores"
        placeholder={STORE_SEARCH_PLACEHOLDER}
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(event) => onSearchChange(event.currentTarget.value)}
        style={{ flex: "2 1 240px" }}
        miw={200}
      />
      <MultiSelect
        aria-label="Filter by retailer"
        placeholder={retailers.length ? undefined : "All retailers"}
        data={retailerOptions}
        value={retailers}
        onChange={onRetailersChange}
        disabled={retailerOptions.length === 0}
        // Selected retailers drop out of the list, so the menu shrinks as the
        // pills grow instead of both competing for space.
        hidePickedOptions
        clearable
        searchable
        style={{ flex: "3 1 280px" }}
        miw={220}
      />
      <Select
        aria-label="Filter by status"
        placeholder="All statuses"
        data={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
        value={status}
        onChange={(value) => onStatusChange(value as StoreStatus | null)}
        disabled={!statusKnown}
        clearable
        style={{ flex: "0 1 150px" }}
        miw={130}
      />
      <Select
        aria-label="Filter by timezone"
        placeholder="All timezones"
        data={timezoneOptions}
        value={timezone}
        onChange={onTimezoneChange}
        clearable
        searchable
        style={{ flex: "1 1 200px" }}
        miw={170}
      />
    </Group>
  );
}
